import React, { createRef, useEffect } from 'react';
import { useCountriesList, useSettings } from '../../hooks/queryHooks';

import styles from './bankSelectionPage.module.scss';
import { useTranslation } from 'react-i18next';
import SearchInput from '../../components/forms/SearchInput';
import BankResultsContainer from './BankResultsContainer';
import CountryDropdown from './CountryDropdownContainer';
import { useBankSearchState, useStepsState } from '../../hooks/zustandHooks';
import Spinner from '../../components/spinner/Spinner';
import Container from '../../components/container/Container';
import BackButton from '../../components/buttons/BackButton';
import { exitApp } from '../../hooks/utils';
import { useLocation } from 'react-router';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';

interface LocationState {
  hasFailedVerification?: boolean;
  hasFailedFetchingTuAccounts?: boolean;
  bankName?: string;
}

const BankSelect = () => {
  const { state: { hasFailedVerification = null, hasFailedFetchingTuAccounts } = {} } = useLocation<LocationState>();
  const searchInput = createRef<HTMLInputElement>();
  const { settings } = useSettings();

  const { t } = useTranslation();
  const setActiveStep = useStepsState(state => state.setActiveStep);

  const { bankSearch, setBankSearch } = useBankSearchState();

  useEffect(() => {
    setActiveStep({ stepIndex: 0 });
    if (window.hj) {
      window.hj('tagRecording', ['Screen: Select Your Bank']);
    }

    if (hasFailedVerification) trackEvent({ type: EventTypes.CIS_VERIFICATION_FAILED });

    if (hasFailedFetchingTuAccounts) trackEvent({ type: EventTypes.CIS_ACCOUNTS_FETCH_FAILED });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isCountriesListLoading } = useCountriesList();

  if (isCountriesListLoading) {
    return (
      <div className={`h-full grid place-content-center text-center`}>
        <Spinner /> <span className="mt-2">{t('Loading...')}</span>
      </div>
    );
  }
  const cisJourneyFailed = hasFailedVerification || hasFailedFetchingTuAccounts;
  return (
    <Container
      className="flex flex-col"
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => exitApp()
        }
      })}
    >
      <div className={styles.bankSelectContainerContent}>
        {!cisJourneyFailed && <BackButton />}

        <h1 className="text-[18px] xl:text-lg font-semi-bold">
          {hasFailedVerification
            ? t<string>('We were unable to verify your identity')
            : t<string>('Connect your bank account')}
        </h1>

        <p
          className={`text-base ${cisJourneyFailed ? '' : 'hidden'} ${hasFailedFetchingTuAccounts && 'text-negative'}`}
          data-testid="bank_select_no_verify_paragraph"
        >
          {hasFailedFetchingTuAccounts
            ? t(
                `We weren’t able to fetch account data from TransUnion. Please connect all necessary accounts you hold.`
              )
            : t(
                `Search for your bank or select it from our common banks list; if you can't find it, please double check your spelling and then let us know if it's unlisted.`
              )}
        </p>

        <div className={`${styles.searchWrapper} flex mb-3`}>
          <div className={styles.countryDropdown}>
            <CountryDropdown />
          </div>
          <div className={`${styles.searchInput} w-full`}>
            <SearchInput
              forwardedRef={searchInput}
              placeholder={t<string>('Search')}
              value={bankSearch}
              onChangeValue={e => setBankSearch(e.currentTarget.value)}
              data-testid="bank_search_input"
            />
          </div>
        </div>
      </div>
      <BankResultsContainer />
    </Container>
  );
};

export default BankSelect;
